import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEvent, useStoreMap, useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useNavigateToCreatePortal } from '@nx-frontend/shared/hooks/use-navigate-to-create-portal';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  $menuState,
  searchMenuItemByIdFx,
  setExpandedParentsByMenuItemIdFx,
} from '@nx-frontend/shared/store/feature-menu-tree';
import { $portalSettings, $portalSettingsLoading } from '@nx-frontend/shared/store/portal';
import { setIsPortalSettingsCollapsedAndSaveFx } from '@nx-frontend/shared/store/sidebar';
import { ControlbarEditEntityType, ControlbarEditParams, MenusState, MenuType } from '@nx-frontend/shared/types';
import {
  addHttpsToUrl,
  getFirstLetters,
  replaceNimbusDomains,
  scrollToSidebarMenuItem,
} from '@nx-frontend/shared/utils';
import { useExpandPortalMenu } from '../hooks';
import {
  CustomizerAdditionalLinkTypes,
  getCustomizerAdditionalLink,
  getCustomizerAdditionalLinkId,
  SidebarPortalLinks,
} from './sidebar-portal-links';
import { SidebarPortalMenu } from './sidebar-portal-menu';

export function SidebarPortalSection() {
  const t = useTranslations('sidebar');
  const [isInitialScroll, setIsInitialScroll] = useState(true);
  const [portalSettings, currentWorkspace, portalSettingsLoading] = useUnit([
    $portalSettings,
    $currentWorkspace,
    $portalSettingsLoading,
  ]);
  const [searchMenuItemById, setExpandedParentsByMenuItemId, setIsPortalSettingsCollapsedAndSave] = useEvent([
    searchMenuItemByIdFx,
    setExpandedParentsByMenuItemIdFx,
    setIsPortalSettingsCollapsedAndSaveFx,
  ]);

  const activeMenuItem = useStoreMap({
    keys: [],
    defaultValue: null,
    store: $menuState,
    fn: (state: MenusState) => {
      const menuType = [MenuType.PORTAL_TOPBAR, MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_FOOTER].find(
        (menuType) => state[menuType].activeItemIds.length
      );
      if (menuType) {
        const id = state[menuType].activeItemIds[0];
        return state[menuType].entities[id];
      }
      return null;
    },
  });
  const [activeScrollId, setActiveScrollId] = useState<string | null>(null);

  const currentPath = usePathname();
  const searchParams = useSearchParams();
  const { setMenuExpanded } = useExpandPortalMenu();
  const entityId = searchParams.get(ControlbarEditParams.ENTITY_ID);

  const openMenuByItemId = useCallback(
    async (itemId: string) => {
      const itemResult = await searchMenuItemById({
        id: itemId,
        menuTypes: [MenuType.PORTAL_TOPBAR, MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_FOOTER],
      });
      if (itemResult) {
        setMenuExpanded({ menuType: itemResult.menuType, value: true });
      }
    },
    [searchMenuItemById, setMenuExpanded]
  );

  useEffect(() => {
    const allPaths = Object.values(CustomizerAdditionalLinkTypes).map((type) => {
      return {
        type,
        url: getCustomizerAdditionalLink(type, portalSettings.id),
      };
    });
    let isSettings = false;
    const activePath = allPaths.find((item) => item.url === currentPath);
    let scrollId: string | null = null;
    if (activePath) {
      isSettings = true;
      scrollId = getCustomizerAdditionalLinkId(activePath.type, portalSettings.id);
    } else if (searchParams.get(ControlbarEditParams.ENTITY_TYPE) === ControlbarEditEntityType.THEME) {
      isSettings = true;
      scrollId = getCustomizerAdditionalLinkId(CustomizerAdditionalLinkTypes.PORTAL_STYLE, portalSettings.id);
    } else if (entityId) {
      scrollId = entityId;
      isSettings = false;
    } else if (activeMenuItem) {
      scrollId = activeMenuItem.id;
      isSettings = false;
    }
    if (activeScrollId !== scrollId) {
      if (isSettings) {
        setIsPortalSettingsCollapsedAndSave(false);
      }
      if (scrollId) {
        openMenuByItemId(scrollId);
        setExpandedParentsByMenuItemId({
          menuItemId: scrollId,
          menuTypes: [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_FOOTER, MenuType.PORTAL_TOPBAR],
        });
      }
      setActiveScrollId(scrollId);
    }
  }, [
    activeMenuItem,
    activeScrollId,
    currentPath,
    entityId,
    openMenuByItemId,
    portalSettings.id,
    searchMenuItemById,
    searchParams,
    setExpandedParentsByMenuItemId,
    setIsPortalSettingsCollapsedAndSave,
    setMenuExpanded,
  ]);

  useEffect(() => {
    if (activeScrollId) {
      setTimeout(() => {
        scrollToSidebarMenuItem(activeScrollId, isInitialScroll ? 'instant' : 'smooth');
        setIsInitialScroll(false);
      });
    }
  }, [activeScrollId, isInitialScroll]);

  const portalUrl = useMemo(() => {
    if (portalSettings.domain) {
      return addHttpsToUrl(replaceNimbusDomains(portalSettings.domain, 'portal'));
    }
    return '';
  }, [portalSettings.domain]);

  const portalExists = !!portalSettings.id;

  const navigateToCreatePortal = useNavigateToCreatePortal();

  const handleNewPortalWorkspaceClick = () => {
    navigateToCreatePortal({ workspaceId: currentWorkspace.globalId });
  };

  const customizerUrl = useMemo(() => {
    return `/portal/${portalSettings.id}`;
  }, [portalSettings.id]);

  const firstLetters = portalSettings.name ? getFirstLetters(portalSettings.name) : '';
  const analyticsUrl = `${customizerUrl}/analytics`;

  return (
    <>
      {portalExists && (
        <>
          <div className="flex -ml-4 -mr-4 gap-2 items-center px-4 py-2">
            <div className="flex gap-2 items-center">
              <div className="rounded w-4 h-4 flex align-center justify-center text-[10px] font-semibold bg-cyan-900 text-white uppercase">
                {firstLetters}
              </div>
              <span className="text-sm">{portalSettings.name}</span>
            </div>
            <Link href={portalUrl} target="_blank" className="ml-auto cursor-pointer">
              <Icon className="w-4 h-4 text-cyan-900" icon={IconsMap.OPEN} />
            </Link>
          </div>

          <Link
            href={analyticsUrl}
            className={classNames('pl-4 mt-2 flex gap-2 cursor-pointer items-center text-sm py-2 rounded', {
              'bg-teal-100 font-bold': analyticsUrl === currentPath,
            })}
          >
            <span>
              <Icon className="w-4 h-4 text-neutral-500" icon={IconsMap.BAR_CHART} />
            </span>
            <span>{t('sidebar-analytics')}</span>
          </Link>

          <div className="w-full h-[1px] bg-neutral-200 mt-1 mb-1"></div>

          <SidebarPortalMenu />

          <div className="w-full h-[1px] bg-neutral-200 mt-1 mb-1"></div>
          <SidebarPortalLinks />
        </>
      )}

      {!portalExists && !portalSettingsLoading && (
        <div className="flex items-center pl-6">
          <div className="text-[12px] flex-auto">{t('sidebar-no_portals_yet')}</div>
          <div className="flex-auto w-4 h-4 max-w-[16px]" onClick={() => handleNewPortalWorkspaceClick()}>
            <Icon
              icon={IconsMap.PLUS}
              className="w-4 h-4 text-neutral-500 rounded hover:bg-neutral-200 cursor-pointer"
            />
          </div>
        </div>
      )}
    </>
  );
}
